import { useEffect, useId, useState } from 'react';
import { toast } from 'react-toastify';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import countryLabels from 'react-phone-number-input/locale/fr.json';
import 'react-phone-number-input/style.css';
import ApiDatabase from 'server';
import UseContext from 'hooks/useContext';
import useModal from 'components/modal/useModal';
import Modalheader from 'components/modal/components/header';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Button from 'components/button';
import StyledInput from 'components/styledInput/StyledInput';
import Banner from 'components/banner/banner';
import PerimeterChecklistField from 'components/perimeter/PerimeterChecklistField';
import { isUnrestrictedRole } from 'components/perimeter/perimeterMembers';
import {
  defaultNotificationsForRole,
  diffNotificationLevels,
  withPerimeterFallback,
} from 'components/perimeter/memberNotifications';
import CompanyContactNotifications from 'components/SlideOver/dataSlideOver/data/slideOverCompany/CompanyContactNotifications';
import { ChevronRight, WarningCircle } from 'assets/icons';
import {
  COMPANY_ADMIN_ROLE,
  companyRoleDescription,
  companyRoleOptions,
} from 'constants/companyRoles';
import { validateContactForm } from 'helper/companyContactForm';
import { toPhoneValue } from 'helper/companyContactPhone';
import useInvitationPerimeters from './useInvitationPerimeters';

const OBSERVER_ROLE = '200';

// errorCode from the api → [field to highlight, text key under companyContacts]
const API_ERRORS = {
  EMAIL_INVALID: ['email', 'edit.errors.emailInvalid'],
  FIRSTNAME_INVALID: ['firstName', 'edit.errors.required'],
  LASTNAME_INVALID: ['lastName', 'edit.errors.required'],
  ROLE_INVALID: ['role', 'edit.errors.required'],
  ADMIN_ROLE_TAKEN: ['role', 'edit.errors.adminTakenUnknown'],
  PHONE_INVALID: ['phone', 'invite.errors.phoneInvalid'],
  ALREADY_MEMBER: ['email', 'invite.errors.alreadyMember'],
  TEMP_WORKER_ACCOUNT: ['email', 'invite.errors.tempWorker'],
};

// Perimeter refusals: text key under companyContacts.invite.perimeters. The
// api writes nothing when it refuses one, so the invitation did not leave.
const PERIMETER_ERRORS = {
  PERIMETER_MEMBER_NOT_RESTRICTABLE: 'errors.notRestrictable',
  INVALID_PERIMETER_ID: 'errors.notFound',
  PERIMETER_NOT_FOUND: 'errors.notFound',
  PERIMETER_ARCHIVED: 'errors.archived',
};
// The list was outdated: it is read again before the admin retries.
const STALE_PERIMETER_ERRORS = [
  'INVALID_PERIMETER_ID',
  'PERIMETER_NOT_FOUND',
  'PERIMETER_ARCHIVED',
];

// Notification refusals: text key under companyContacts.invite.notifications.
// Like the perimeter ones, the api refuses them before writing anything.
const NOTIFICATION_ERRORS = {
  NOTIFICATION_TYPE_INVALID: 'errors.typeInvalid',
  NOTIFICATION_LEVEL_INVALID: 'errors.levelInvalid',
};

// The only levels a prefill may carry: the billing contact modal (JBWEB-1285)
// hands over its Factures and Relances boxes, nothing else.
const PREFILLABLE_NOTIFICATION_TYPES = ['invoices', 'reminders'];
const PREFILLABLE_NOTIFICATION_LEVELS = ['none', 'all'];

const prefilledNotificationLevels = (notifications) =>
  PREFILLABLE_NOTIFICATION_TYPES.reduce(
    (levels, type) =>
      PREFILLABLE_NOTIFICATION_LEVELS.includes(notifications?.[type])
        ? { ...levels, [type]: notifications[type] }
        : levels,
    {},
  );

const STEP_INFORMATION = 'information';
const STEP_NOTIFICATIONS = 'notifications';

const ModalAddCompanyMember = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn, language } = UseContext();
  const text = (key) => language.getValue(`companyContacts.${key}`, '');
  const t = (key) => text(`invite.${key}`);
  const tp = (key) => t(`perimeters.${key}`);
  const tn = (key) => t(`notifications.${key}`);
  const fromHubspot = modalData?.origin === 'hubspot';
  // The modal is mounted anew on each opening: the initial state is enough to
  // start from the contact picked with « Inviter ».
  const prefill = modalData?.prefill ?? {};
  const [form, setForm] = useState({
    email: prefill.email ?? '',
    firstName: prefill.firstName ?? '',
    lastName: prefill.lastName ?? '',
    phone: toPhoneValue(prefill.phone) ?? '',
    role: OBSERVER_ROLE,
  });
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const phoneFieldId = useId();
  const perimeterList = useInvitationPerimeters(modalData?.idCompany);
  // The perimeters field only shows when the company has an active perimeter;
  // the notifications step is always there.
  const hasPerimeterField = perimeterList.hasPerimeters;
  const isLoadingPerimeters =
    perimeterList.status === 'loading' && !hasPerimeterField;
  const [step, setStep] = useState(STEP_INFORMATION);
  const [perimeterIds, setPerimeterIds] = useState([]);
  const [perimeterError, setPerimeterError] = useState('');
  // Only the subjects the admin touched (or the billing modal handed over),
  // over the defaults of the role.
  const [draftNotificationLevels, setDraftNotificationLevels] = useState(() =>
    prefilledNotificationLevels(prefill.notifications),
  );
  const [notificationError, setNotificationError] = useState('');
  const isUnrestricted = isUnrestrictedRole(form.role);

  // A perimeter gone from a reloaded list is no longer offered nor sent.
  const activePerimeters = perimeterList.perimeters;
  useEffect(() => {
    setPerimeterIds((previous) =>
      previous.filter((id) =>
        activePerimeters.some((perimeter) => perimeter.id === id),
      ),
    );
  }, [activePerimeters]);

  const roleList = companyRoleOptions(language).filter(
    (option) => Number(option.value) !== COMPANY_ADMIN_ROLE,
  );
  const inviteeName =
    [form.firstName.trim(), form.lastName.trim().toUpperCase()]
      .filter(Boolean)
      .join(' ') || t('someone');
  const description = t(
    fromHubspot ? 'hubspotDescription' : 'description',
  ).replace('{name}', () => inviteeName);
  const errorText = (key) => (key ? text(key) : undefined);

  const update = (field) => (eventOrValue) => {
    const value = eventOrValue?.target
      ? eventOrValue.target.value
      : (eventOrValue ?? '');
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  const updatePerimeterIds = (ids) => {
    setPerimeterIds(ids);
    setPerimeterError('');
    setNotificationError('');
  };

  const changeNotificationLevel = (type, level) => {
    setNotificationError('');
    setDraftNotificationLevels((previous) => ({ ...previous, [type]: level }));
  };

  const onSent = (data) => {
    setIsSending(false);
    if (!data?.user) {
      toast.error(text('edit.errors.generic'));
      return;
    }
    // A pending existing account is sent the invitation link, as a new one is;
    // only an active one is told it was attached, by a notice without a link.
    const wasInvited = !data.existingAccount || data.user.validation === 0;
    if (data.invitationSent === false) {
      toast.warning(t(wasInvited ? 'sentMailFailed' : 'attachedMailFailed'));
    } else {
      toast.success(t(wasInvited ? 'sent' : 'attached'));
    }
    // The company travels with the return: it can outlive the panel it was
    // sent from, and only that company's members list may take it in.
    setModalDataReturn({
      type: 'employee_created',
      user: data.user,
      idCompany: modalData?.idCompany,
    });
    toggle();
  };

  const onFailed = (err) => {
    setIsSending(false);
    if (err?.name === 'CanceledError') return;
    const errorCode = err?.response?.data?.errorCode;
    if (errorCode === 'COMPANY_NOT_FOUND') {
      toast.error(t('errors.companyNotFound'));
      toggle();
      return;
    }
    const known = API_ERRORS[errorCode];
    if (known) {
      setErrors({ [known[0]]: known[1] });
      setStep(STEP_INFORMATION);
      return;
    }
    if (PERIMETER_ERRORS[errorCode]) {
      setPerimeterError(tp(PERIMETER_ERRORS[errorCode]));
      setStep(STEP_NOTIFICATIONS);
      if (STALE_PERIMETER_ERRORS.includes(errorCode)) perimeterList.reload();
      return;
    }
    if (NOTIFICATION_ERRORS[errorCode]) {
      setNotificationError(tn(NOTIFICATION_ERRORS[errorCode]));
      setStep(STEP_NOTIFICATIONS);
      // A perimeter level refused: the perimeters it relied on may be gone.
      if (errorCode === 'NOTIFICATION_LEVEL_INVALID') perimeterList.reload();
      return;
    }
    toast.error(text('edit.errors.generic'));
  };

  // true when the first step's fields can be sent.
  const validateInformation = () => {
    const clientErrors = Object.fromEntries(
      Object.entries(validateContactForm(form)).map(([field, key]) => [
        field,
        `edit.errors.${key}`,
      ]),
    );
    if (form.phone && !isValidPhoneNumber(form.phone)) {
      clientErrors.phone = 'invite.errors.phoneInvalid';
    }
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      setStep(STEP_INFORMATION);
      return false;
    }
    return true;
  };

  const goToNotifications = () => {
    if (validateInformation()) setStep(STEP_NOTIFICATIONS);
  };

  // Only the perimeters still in the list, and none for a role that is never
  // restricted: the api refuses a non-empty list for it.
  const sentPerimeterIds =
    hasPerimeterField && !isUnrestricted
      ? perimeterIds.filter((id) =>
          activePerimeters.some((perimeter) => perimeter.id === id),
        )
      : [];

  // The api refuses 'perimeter' for a Director or an Administrator, and for an
  // invitation without a perimeter: the table shows such a level as 'all',
  // as the contact screen does, and never sends it.
  const isPerimeterLevelBlocked =
    isUnrestricted || sentPerimeterIds.length === 0;
  const roleDefaultLevels = defaultNotificationsForRole(form.role);
  const mergedNotificationLevels = {
    ...roleDefaultLevels,
    ...draftNotificationLevels,
  };
  const shownNotificationLevels = isPerimeterLevelBlocked
    ? withPerimeterFallback(mergedNotificationLevels)
    : mergedNotificationLevels;
  // Only what differs from the role's defaults: the api reads those on the
  // fly, and an invitation the admin left as it was sends no notifications.
  const notificationPatch = diffNotificationLevels(
    roleDefaultLevels,
    shownNotificationLevels,
  );
  const notificationHint = () => {
    if (isUnrestricted) return text('edit.notifications.unrestrictedHint');
    if (perimeterList.status === 'error' && !hasPerimeterField) return '';
    if (isLoadingPerimeters) return '';
    if (!hasPerimeterField) return tn('noActivePerimeterHint');
    if (sentPerimeterIds.length === 0) return tn('noPerimeterChosenHint');
    return '';
  };

  const submit = () => {
    if (!validateInformation()) return;
    setPerimeterError('');
    setNotificationError('');
    setIsSending(true);
    ApiDatabase.postAddCompanyMember(
      {
        idCompany: modalData.idCompany,
        employee: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          role: Number(form.role),
          ...(form.phone ? { phone: form.phone } : {}),
          ...(sentPerimeterIds.length
            ? { perimeterIds: sentPerimeterIds }
            : {}),
          ...(Object.keys(notificationPatch).length
            ? { notifications: notificationPatch }
            : {}),
        },
        origin: fromHubspot ? 'hubspot' : 'manual',
        ...(fromHubspot && modalData.hubspotContactId
          ? { hubspotContactId: modalData.hubspotContactId }
          : {}),
      },
      onSent,
      onFailed,
    );
  };

  const roleDescription = companyRoleDescription(language, form.role);
  const isInformationStep = step === STEP_INFORMATION;
  // Sending before the list is read would skip a field the admin may need.
  const isPrimaryDisabled =
    isSending || (!isInformationStep && isLoadingPerimeters);
  const stepClassName = (isCurrent, isDone) =>
    `rounded-md border px-3 py-2 text-sm ${
      isCurrent
        ? 'border-yellow-300 bg-yellow-50 text-yellow-600'
        : isDone
          ? 'border-transparent bg-yellow-50 text-yellow-600'
          : 'border-transparent bg-gray-50 text-gray-700'
    }`;

  return (
    <>
      <Modalheader hide={toggle} hideShow={!isSending}>
        {t('title')}
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-4 text-sm'}>
          <p className={'text-sm text-gray-500'}>{description}</p>
          <nav aria-label={tp('steps')} className={'flex items-center gap-3'}>
            <button
              type={'button'}
              aria-current={isInformationStep ? 'step' : undefined}
              disabled={isSending}
              onClick={() => setStep(STEP_INFORMATION)}
              className={stepClassName(isInformationStep, !isInformationStep)}
            >
              {tp('information')}
            </button>
            <ChevronRight wh={16} color={'#6B7280'} />
            <button
              type={'button'}
              aria-current={isInformationStep ? undefined : 'step'}
              disabled={isSending}
              onClick={goToNotifications}
              className={stepClassName(!isInformationStep, false)}
            >
              {tp('notifications')}
            </button>
          </nav>
          {isInformationStep ? (
            <>
              {perimeterList.status === 'error' && !hasPerimeterField ? (
                <div className={'flex flex-col gap-2'}>
                  <Banner type={'warning'}>{tp('loadError')}</Banner>
                  <button
                    type={'button'}
                    onClick={perimeterList.retry}
                    className={'self-start text-sm text-yellow-600 underline'}
                  >
                    {tp('retry')}
                  </button>
                </div>
              ) : null}
              <StyledInput
                label={t('email')}
                type={'email'}
                value={form.email}
                onChange={update('email')}
                error={errorText(errors.email)}
              />
              <div className={'grid grid-cols-2 gap-3'}>
                <StyledInput
                  label={t('firstName')}
                  type={'text'}
                  value={form.firstName}
                  onChange={update('firstName')}
                  error={errorText(errors.firstName)}
                />
                <StyledInput
                  label={t('lastName')}
                  type={'text'}
                  value={form.lastName}
                  onChange={update('lastName')}
                  error={errorText(errors.lastName)}
                />
              </div>
              <div className={'flex flex-col gap-1 min-w-0 w-full'}>
                <label htmlFor={phoneFieldId} className={'ft-sm text-gray-500'}>
                  {t('phone')}
                </label>
                <PhoneInput
                  // `international` stays unset so that a pasted "+590…" is kept as
                  // well as a national number (`international={false}` drops the
                  // "+"); `initialValueFormat` shows a prefill in national format,
                  // like a typed number, the flag naming its country.
                  defaultCountry={'FR'}
                  initialValueFormat={'national'}
                  flags={flags}
                  labels={countryLabels}
                  value={form.phone || undefined}
                  onChange={update('phone')}
                  // 9.4 and 8.5 draw the flag inside the field's box: the border
                  // goes on the library's wrapper, not on its input.
                  className={
                    'flex items-center gap-2 h-10 border rounded-md px-2 focus-within:border-yellow-500'
                  }
                  // The library derives these two from --PhoneInput-color--focus in
                  // :root, where the value is already resolved: setting that
                  // variable here would change nothing.
                  style={{
                    '--PhoneInputCountrySelectArrow-color--focus': '#ff9600',
                    '--PhoneInputCountryFlag-borderColor--focus': '#ff9600',
                  }}
                  numberInputProps={{
                    id: phoneFieldId,
                    className:
                      'flex-1 min-w-0 h-full text-sm bg-transparent border-0 focus:outline-none',
                  }}
                />
                {errors.phone && (
                  <div
                    className={'text-red-500 flex items-center gap-1 text-xs'}
                  >
                    <WarningCircle wh={16} color={'#EF4444'} />
                    {errorText(errors.phone)}
                  </div>
                )}
              </div>
              <div className={'flex flex-col gap-1'}>
                <StyledInput
                  label={t('role')}
                  type={'select'}
                  value={form.role}
                  list={roleList}
                  onChange={update('role')}
                  error={errorText(errors.role)}
                />
                {roleDescription && (
                  <div className={'text-gray-500 ft-sm mt-2'}>
                    {roleDescription}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={'flex flex-col gap-4'}>
              <div className={'flex flex-col gap-2'}>
                {hasPerimeterField ? (
                  <PerimeterChecklistField
                    label={tp('label')}
                    perimeters={activePerimeters}
                    selectedIds={perimeterIds}
                    onChange={updatePerimeterIds}
                    placeholder={tp('none')}
                    emptyText={tp('empty')}
                    isDisabled={isUnrestricted}
                    disabledText={tp('unrestricted')}
                  />
                ) : null}
                {perimeterError ? (
                  <Banner type={'alert'}>{perimeterError}</Banner>
                ) : null}
                {perimeterList.status === 'error' ? (
                  <div className={'flex flex-col gap-2'}>
                    <Banner type={'warning'}>
                      {tp(hasPerimeterField ? 'reloadError' : 'loadError')}
                    </Banner>
                    <button
                      type={'button'}
                      onClick={perimeterList.retry}
                      className={'self-start text-sm text-yellow-600 underline'}
                    >
                      {tp('retry')}
                    </button>
                  </div>
                ) : null}
              </div>
              <CompanyContactNotifications
                status={'ready'}
                levels={shownNotificationLevels}
                isPerimeterColumnDisabled={isPerimeterLevelBlocked}
                perimeterHint={notificationHint()}
                error={notificationError}
                onChange={changeNotificationLevel}
              />
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          size={'LG'}
          color={'SECONDARY'}
          disabled={isSending}
          onClick={toggle}
        >
          {t('cancel')}
        </Button>
        <Button
          size={'LG'}
          color={isPrimaryDisabled ? 'DISABLED' : 'PRIMARY'}
          disabled={isPrimaryDisabled}
          onClick={isInformationStep ? goToNotifications : submit}
        >
          {isSending
            ? t('sending')
            : isInformationStep
              ? tp('next')
              : t('send')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalAddCompanyMember;
